import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../services/AuthService';
import UserTypeService from '../services/UserTypeService';

/**
 * Custom hook for managing user type detection and validation
 * @returns {Object} User type data and utility functions
 */
export const useUserType = () => {
  const { user, isAuthenticated } = useAuth();
  const [userTypeData, setUserTypeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Memoize the service so it's only created once, preserving cache across renders
  const userTypeService = useMemo(() => new UserTypeService(), []);

  useEffect(() => {
    const fetchUserType = async () => {
      if (!isAuthenticated || !user?.sub) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const result = await userTypeService.getUserType(user.sub);
        setUserTypeData(result);
      } catch (err) {
        console.error('Error fetching user type:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub, isAuthenticated, userTypeService]);

  /**
   * Refresh user type data
   */
  const refreshUserType = async () => {
    if (!user?.sub) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Clear cached data so we fetch fresh from the API
      UserTypeService.clearCache(user.sub);
      
      const result = await userTypeService.getUserType(user.sub);
      setUserTypeData(result);
    } catch (err) {
      console.error('Error refreshing user type:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validate if user can perform company actions
   */
  const validateCompanyAccess = async () => {
    if (!user?.sub) return { canPerform: false, reason: 'User not authenticated' };
    return await userTypeService.validateCompanyAccess(user.sub);
  };

  /**
   * Validate if user can perform job seeker actions
   */
  const validateJobSeekerAccess = async () => {
    if (!user?.sub) return { canPerform: false, reason: 'User not authenticated' };
    return await userTypeService.validateJobSeekerAccess(user.sub);
  };

  /**
   * Get appropriate dashboard route
   */
  const getDashboardRoute = async () => {
    if (!user?.sub) return '/onboarding';
    return await userTypeService.getDashboardRoute(user.sub);
  };

  return {
    // Data
    userTypeData,
    loading,
    error,
    
    // Computed values
    userType: userTypeData?.userType || UserTypeService.USER_TYPES.UNKNOWN,
    isCompany: userTypeData?.userType === UserTypeService.USER_TYPES.COMPANY,
    isJobSeeker: userTypeData?.userType === UserTypeService.USER_TYPES.JOB_SEEKER,
    hasCompanyProfile: userTypeData?.hasCompanyProfile || false,
    hasJobSeekerProfile: userTypeData?.hasJobSeekerProfile || false,
    isProfileDefined: (userTypeData?.hasCompanyProfile || userTypeData?.hasJobSeekerProfile) || false,
    
    // Utility functions
    refreshUserType,
    validateCompanyAccess,
    validateJobSeekerAccess,
    getDashboardRoute,
    
    // Constants
    USER_TYPES: UserTypeService.USER_TYPES
  };
};

export default useUserType;